/**
 * CmsGuides — cornerstone guide editor.
 *
 * Non-engineers can edit sections, sources, related guides, journey
 * assignment, author byline, status, and upload the hero image PNG.
 *
 * Data lives in the ``fp_cornerstone_guides`` collection (seeded on
 * boot and modified via /api/admin/food-pulse/guides).
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Save, Plus, Trash2, BookOpen, Loader2, ImagePlus, Upload, X,
} from "lucide-react";

const JOURNEYS = ["discover", "understand", "nearby", "share", "cook"];
const STATUSES = ["live", "beta", "coming-soon", "draft"];

const EMPTY = {
  slug: "",
  title: "",
  journey: "share",
  status: "live",
  summary: "",
  sections: [],
  sources: [],
  related: [],
  last_reviewed: "",
  author_slug: null,
};

export default function CmsGuides() {
  const [list, setList] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [l, c] = await Promise.all([
        http.get("/admin/food-pulse/guides"),
        http.get("/admin/food-pulse/contributors").catch(() => ({ data: { items: [] } })),
      ]);
      setList(l.data?.items || []);
      setContributors(c.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load guides");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditing("__new__");
  };
  const openEdit = async (slug) => {
    try {
      const r = await http.get(`/admin/food-pulse/guides/${slug}`);
      const g = r.data;
      setForm({
        slug: g.slug,
        title: g.title,
        journey: g.journey,
        status: g.status,
        summary: g.summary || "",
        sections: g.sections || [],
        sources: g.sources || [],
        related: g.related || [],
        last_reviewed: g.last_reviewed || "",
        author_slug: g.author_slug || null,
      });
      setEditing(slug);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load guide");
    }
  };

  const save = async () => {
    if (!form.slug || !form.title) {
      toast.error("Slug and title are required");
      return;
    }
    setBusy(true);
    try {
      if (editing === "__new__") {
        await http.post("/admin/food-pulse/guides", form);
        toast.success("Guide created");
      } else {
        await http.put(`/admin/food-pulse/guides/${form.slug}`, form);
        toast.success("Guide saved");
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (slug) => {
    if (!confirm(`Delete guide "${slug}"?`)) return;
    try {
      await http.delete(`/admin/food-pulse/guides/${slug}`);
      toast.success("Guide deleted");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (editing) {
    return (
      <GuideEditor
        form={form}
        setForm={setForm}
        contributors={contributors}
        onSave={save}
        busy={busy}
        onCancel={() => setEditing(null)}
        isNew={editing === "__new__"}
      />
    );
  }

  return (
    <div className="max-w-4xl" data-testid="cms-guides">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Cornerstone guides</h1>
          <p className="mt-1 text-sm text-slate-500">
            Edit the ten authoritative editorial guides that anchor FoodPulse's
            journeys. Sections, sources, related slugs, journey mapping, byline,
            and hero images are all editable here.
          </p>
        </div>
        <Button
          onClick={openNew}
          data-testid="cms-guides-new"
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="mr-1 h-4 w-4" /> New guide
        </Button>
      </div>

      {loading ? (
        <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="mt-6 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
          {list.map((g) => (
            <div
              key={g.slug}
              className="flex items-center justify-between gap-3 p-4"
              data-testid={`cms-guide-row-${g.slug}`}
            >
              <div className="flex items-center gap-3">
                {g.hero_image_url ? (
                  <img src={g.hero_image_url} alt="" className="h-10 w-10 rounded-md object-cover" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-400">
                    <BookOpen className="h-4 w-4" />
                  </div>
                )}
                <div>
                  <div className="text-sm font-semibold text-slate-900">{g.title}</div>
                  <div className="text-xs text-slate-500">
                    {g.journey} · {g.slug} · <span className={g.status === "live" ? "text-emerald-600" : "text-amber-600"}>{g.status}</span>
                    {g.author_slug ? ` · byline: ${g.author_slug}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(g.slug)}
                  data-testid={`cms-guide-edit-${g.slug}`}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => remove(g.slug)}
                  data-testid={`cms-guide-delete-${g.slug}`}
                  className="text-rose-700 hover:bg-rose-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GuideEditor({ form, setForm, contributors, onSave, busy, onCancel, isNew }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addSection = () =>
    setField("sections", [...(form.sections || []), { heading: "", body: "" }]);
  const updateSection = (i, patch) =>
    setField(
      "sections",
      form.sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s))
    );
  const removeSection = (i) =>
    setField("sections", form.sections.filter((_, idx) => idx !== i));

  const uploadHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await http.put(`/admin/food-pulse/guides/${form.slug}/hero`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Hero image uploaded — refresh preview to see it");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeHero = async () => {
    if (!confirm("Remove hero image?")) return;
    try {
      await http.delete(`/admin/food-pulse/guides/${form.slug}/hero`);
      toast.success("Hero removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-3xl" data-testid="cms-guide-editor">
      <button
        onClick={onCancel}
        className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to list
      </button>
      <h1 className="text-2xl font-semibold text-slate-900">
        {isNew ? "New cornerstone guide" : `Edit ${form.title || form.slug}`}
      </h1>

      <div className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Slug</Label>
            <Input
              value={form.slug}
              onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9\-]/g, "-"))}
              disabled={!isNew}
              data-testid="cms-guide-slug"
            />
          </div>
          <div>
            <Label className="text-xs">Journey</Label>
            <select
              value={form.journey}
              onChange={(e) => setField("journey", e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm"
              data-testid="cms-guide-journey"
            >
              {JOURNEYS.map((j) => <option key={j} value={j}>{j}</option>)}
            </select>
          </div>
        </div>

        <div>
          <Label className="text-xs">Title</Label>
          <Input value={form.title} onChange={(e) => setField("title", e.target.value)} data-testid="cms-guide-title" />
        </div>

        <div>
          <Label className="text-xs">Summary</Label>
          <Textarea rows={3} value={form.summary} onChange={(e) => setField("summary", e.target.value)} data-testid="cms-guide-summary" />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <Label className="text-xs">Status</Label>
            <select
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm"
              data-testid="cms-guide-status"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Last reviewed</Label>
            <Input
              value={form.last_reviewed}
              onChange={(e) => setField("last_reviewed", e.target.value)}
              placeholder="YYYY-MM-DD"
              data-testid="cms-guide-last-reviewed"
            />
          </div>
          <div>
            <Label className="text-xs">Author byline</Label>
            <select
              value={form.author_slug || ""}
              onChange={(e) => setField("author_slug", e.target.value || null)}
              className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm"
              data-testid="cms-guide-author"
            >
              <option value="">(No byline)</option>
              {contributors.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name} ({c.role})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Sections */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <Label className="text-xs">Sections</Label>
            <Button size="sm" variant="outline" onClick={addSection} data-testid="cms-guide-add-section">
              <Plus className="mr-1 h-3 w-3" /> Add section
            </Button>
          </div>
          <div className="space-y-3">
            {(form.sections || []).map((s, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`cms-guide-section-${i}`}>
                <div className="mb-2 flex items-center justify-between">
                  <Input
                    value={s.heading}
                    onChange={(e) => updateSection(i, { heading: e.target.value })}
                    placeholder="Section heading"
                  />
                  <Button size="sm" variant="outline" onClick={() => removeSection(i)} className="ml-2 text-rose-700">
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <Textarea
                  rows={4}
                  value={s.body}
                  onChange={(e) => updateSection(i, { body: e.target.value })}
                  placeholder="Section body"
                />
              </div>
            ))}
          </div>
        </div>

        <ArrayField label="Sources" items={form.sources || []} onChange={(v) => setField("sources", v)} testid="cms-guide-sources" />
        <ArrayField label="Related guide slugs" items={form.related || []} onChange={(v) => setField("related", v)} testid="cms-guide-related" placeholder="e.g. food-waste-at-home" />

        {!isNew && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-xs">Hero image</Label>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="cms-guide-upload-hero">
                  {uploading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Upload className="mr-1 h-3 w-3" />}
                  Upload PNG/JPG
                </Button>
                <Button size="sm" variant="outline" onClick={removeHero} className="text-rose-700">
                  Remove
                </Button>
              </div>
            </div>
            <input ref={fileRef} type="file" hidden accept="image/png,image/jpeg,image/webp" onChange={uploadHero} />
            <p className="text-[11px] text-slate-500">
              Save the guide first if this is a new slug — the hero endpoint
              needs the slug to exist. Regeneration via GPT Image 1 is
              available from the terminal:{" "}
              <code className="rounded bg-slate-100 px-1">
                python3 /app/backend/scripts/generate_guide_heroes.py --slug {form.slug}
              </code>
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            onClick={onSave}
            disabled={busy}
            data-testid="cms-guide-save"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            Save guide
          </Button>
        </div>
      </div>
    </div>
  );
}

function ArrayField({ label, items, onChange, testid, placeholder }) {
  const [val, setVal] = useState("");
  return (
    <div data-testid={testid}>
      <Label className="text-xs">{label}</Label>
      <ul className="mt-1 space-y-0.5 text-xs">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-2 rounded bg-slate-50 px-2 py-1">
            <span className="flex-1">{it}</span>
            <button
              onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              className="text-slate-400 hover:text-rose-600"
            >
              <X className="h-3 w-3" />
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex gap-1">
        <Input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={placeholder || "Add and press Enter"}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (val.trim()) { onChange([...items, val.trim()]); setVal(""); }
            }
          }}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => { if (val.trim()) { onChange([...items, val.trim()]); setVal(""); } }}
        >
          Add
        </Button>
      </div>
    </div>
  );
}
